import type { EvaluationTriggerContextProto } from "@noom/noom-contracts/noom_contracts/billing/shared";

import { getURLParams } from "src/utils/urlParams";

export const EVALUATE_TRIGGER_SURFACE = {
  pdp: "pdp",
  placementGroup: "placementGroup",
  shopCheckout: "shopCheckout",
} as const;

export const POST_SIGNUP_TRIGGER_CONTENT = "postSignupCrossSell";

type EvaluateTriggerSurface =
  (typeof EVALUATE_TRIGGER_SURFACE)[keyof typeof EVALUATE_TRIGGER_SURFACE];

type BuildEvaluateTriggerContextArgs = {
  triggerSurface: EvaluateTriggerSurface;
  triggerContent: string;
  includeReferringSource?: boolean;
};

function kebabToPascal(value: string) {
  return value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

/** Mixpanel trigger_content: pdp + PascalCase of the product name, not billing product_name or pageId. */
export function toPdpTriggerContent(productName: string) {
  return `pdp${kebabToPascal(productName)}`;
}

function buildEvaluateTriggerContext({
  triggerSurface,
  triggerContent,
  includeReferringSource = false,
}: BuildEvaluateTriggerContextArgs): EvaluationTriggerContextProto {
  const triggerReferringSource = includeReferringSource
    ? getURLParams().upsell_ad_id
    : undefined;

  return {
    triggerSurface,
    triggerContent,
    triggerReferringSource,
  };
}

export function buildPdpEvaluateTriggerContext(
  productName: string
): EvaluationTriggerContextProto {
  return buildEvaluateTriggerContext({
    triggerSurface: EVALUATE_TRIGGER_SURFACE.pdp,
    triggerContent: toPdpTriggerContent(productName),
    includeReferringSource: true,
  });
}

export function buildShopCheckoutEvaluateTriggerContext(
  productName: string
): EvaluationTriggerContextProto {
  return buildEvaluateTriggerContext({
    triggerSurface: EVALUATE_TRIGGER_SURFACE.shopCheckout,
    triggerContent: `shopCheckout${kebabToPascal(productName)}`,
  });
}

export function buildPostSignupEvaluateTriggerContext(): EvaluationTriggerContextProto {
  return buildEvaluateTriggerContext({
    triggerSurface: EVALUATE_TRIGGER_SURFACE.placementGroup,
    triggerContent: POST_SIGNUP_TRIGGER_CONTENT,
  });
}
